function SkeletonBar({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-mono-800/60 rounded ${className}`} />;
}

export function SkeletonRow() {
  return (
    <div className="flex gap-3 px-3 py-2.5">
      <SkeletonBar className="h-3 w-24" />
      <SkeletonBar className="h-3 w-16" />
      <SkeletonBar className="h-3 w-20" />
      <SkeletonBar className="h-3 w-28" />
      <SkeletonBar className="h-3 w-14" />
      <SkeletonBar className="h-3 w-14" />
      <SkeletonBar className="h-3 w-16" />
    </div>
  );
}

export function SkeletonTable({ rows = 8 }: { rows?: number }) {
  return (
    <div className="border border-mono-800 rounded overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="flex gap-3 px-3 py-2.5 bg-mono-900/50 border-b border-mono-800">
        <SkeletonBar className="h-3 w-16" />
        <SkeletonBar className="h-3 w-12" />
        <SkeletonBar className="h-3 w-14" />
        <SkeletonBar className="h-3 w-20" />
        <SkeletonBar className="h-3 w-10" />
        <SkeletonBar className="h-3 w-10" />
        <SkeletonBar className="h-3 w-14" />
      </div>
      {/* Rows */}
      <div className="divide-y divide-mono-800/50">
        {Array.from({ length: rows }, (_, i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    </div>
  );
}

export function SkeletonChart() {
  return (
    <div className="border border-mono-800 rounded p-4 bg-mono-900/50 animate-fade-in">
      <div className="flex items-end gap-1 h-[380px]">
        {/* Y-axis labels */}
        <div className="flex flex-col justify-between h-full py-4 pr-2">
          {[4, 3, 2, 1, 0].map(i => (
            <SkeletonBar key={i} className="h-2 w-8" />
          ))}
        </div>
        {/* Chart area with fake bars/lines */}
        <div className="flex-1 flex items-end gap-[3px] h-full pt-8 pb-6">
          {Array.from({ length: 24 }, (_, i) => {
            const h = 20 + Math.sin(i * 0.5) * 30 + Math.random() * 25;
            return (
              <div
                key={i}
                className="flex-1 animate-pulse rounded-t"
                style={{
                  height: `${h}%`,
                  background: `linear-gradient(to top, rgba(96,165,250,0.15), rgba(96,165,250,0.05))`,
                  animationDelay: `${i * 50}ms`,
                }}
              />
            );
          })}
        </div>
      </div>
      {/* X-axis */}
      <div className="flex gap-4 pl-12 pt-2">
        {Array.from({ length: 6 }, (_, i) => (
          <SkeletonBar key={i} className="h-2 w-10" />
        ))}
      </div>
    </div>
  );
}

export function SkeletonLineChart() {
  return (
    <div className="border border-mono-800 rounded p-4 bg-mono-900/50 animate-fade-in">
      <div className="flex items-end gap-1 h-[380px]">
        <div className="flex flex-col justify-between h-full py-4 pr-2">
          {[4, 3, 2, 1, 0].map(i => (
            <SkeletonBar key={i} className="h-2 w-8" />
          ))}
        </div>
        <div className="flex-1 h-full pt-8 pb-6 relative">
          {/* Horizontal grid lines */}
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="absolute left-0 right-0 border-t border-mono-800/40" style={{ top: `${i * 25}%` }} />
          ))}
          {/* Wavy line path */}
          <svg className="w-full h-full animate-pulse" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path
              d="M0,60 Q10,40 20,50 T40,35 T60,45 T80,30 T100,40"
              fill="none"
              stroke="rgba(96,165,250,0.25)"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d="M0,60 Q10,40 20,50 T40,35 T60,45 T80,30 T100,40 V100 H0 Z"
              fill="rgba(96,165,250,0.06)"
            />
          </svg>
        </div>
      </div>
      <div className="flex gap-4 pl-12 pt-2">
        {Array.from({ length: 6 }, (_, i) => (
          <SkeletonBar key={i} className="h-2 w-10" />
        ))}
      </div>
    </div>
  );
}

export function SkeletonHeatmap() {
  return (
    <div className="border border-mono-800 rounded p-4 bg-mono-900/50 animate-fade-in">
      <div className="space-y-1">
        {Array.from({ length: 6 }, (_, row) => (
          <div key={row} className="flex gap-1">
            <SkeletonBar className="h-5 w-20 shrink-0" />
            {Array.from({ length: 12 }, (_, col) => (
              <div
                key={col}
                className="flex-1 h-5 rounded-sm animate-pulse"
                style={{
                  background: `rgba(96,165,250,${0.05 + Math.sin((row + col) * 0.7) * 0.08})`,
                  animationDelay: `${(row * 12 + col) * 20}ms`,
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonGauge() {
  return (
    <div className="border border-mono-800 rounded p-6 bg-mono-900/50 animate-fade-in flex items-center justify-center">
      <svg className="animate-pulse" width="200" height="120" viewBox="0 0 200 120">
        <path d="M20,110 A80,80 0 0,1 180,110" fill="none" stroke="rgba(96,165,250,0.15)" strokeWidth="16" strokeLinecap="round" />
        <path d="M20,110 A80,80 0 0,1 100,30" fill="none" stroke="rgba(96,165,250,0.08)" strokeWidth="16" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export function SkeletonMap() {
  return (
    <div className="border border-mono-800 rounded bg-mono-900/50 animate-fade-in h-[400px] relative overflow-hidden">
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-20">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={`h-${i}`} className="absolute left-0 right-0 border-t border-mono-700/40" style={{ top: `${(i + 1) * 16.6}%` }} />
        ))}
        {Array.from({ length: 5 }, (_, i) => (
          <div key={`v-${i}`} className="absolute top-0 bottom-0 border-l border-mono-700/40" style={{ left: `${(i + 1) * 16.6}%` }} />
        ))}
      </div>
      {/* Fake well markers */}
      {[
        { x: 30, y: 35 }, { x: 55, y: 25 }, { x: 70, y: 50 },
        { x: 40, y: 65 }, { x: 60, y: 70 }, { x: 25, y: 50 },
      ].map((pos, i) => (
        <div
          key={i}
          className="absolute w-3 h-3 rounded-full animate-pulse bg-mono-700/60"
          style={{ left: `${pos.x}%`, top: `${pos.y}%`, animationDelay: `${i * 100}ms` }}
        />
      ))}
    </div>
  );
}

export function SkeletonScatter() {
  return (
    <div className="border border-mono-800 rounded p-4 bg-mono-900/50 animate-fade-in">
      <div className="flex items-end gap-1 h-[380px]">
        <div className="flex flex-col justify-between h-full py-4 pr-2">
          {[4, 3, 2, 1, 0].map(i => (
            <SkeletonBar key={i} className="h-2 w-8" />
          ))}
        </div>
        <div className="flex-1 h-full pt-8 pb-6 relative">
          {[
            { x: 15, y: 30 }, { x: 25, y: 55 }, { x: 35, y: 20 }, { x: 45, y: 65 },
            { x: 55, y: 40 }, { x: 65, y: 25 }, { x: 75, y: 50 }, { x: 85, y: 35 },
            { x: 20, y: 70 }, { x: 50, y: 15 }, { x: 70, y: 60 }, { x: 40, y: 45 },
          ].map((pos, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-pulse bg-mono-600/40"
              style={{ left: `${pos.x}%`, top: `${pos.y}%`, animationDelay: `${i * 60}ms` }}
            />
          ))}
        </div>
      </div>
      <div className="flex gap-4 pl-12 pt-2">
        {Array.from({ length: 6 }, (_, i) => (
          <SkeletonBar key={i} className="h-2 w-10" />
        ))}
      </div>
    </div>
  );
}

export function SkeletonSparklineGrid() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 animate-fade-in">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="border border-mono-800 rounded p-3 bg-mono-900/50">
          <SkeletonBar className="h-2 w-16 mb-2" />
          <svg className="w-full h-[80px] animate-pulse" viewBox="0 0 100 40" preserveAspectRatio="none" style={{ animationDelay: `${i * 80}ms` }}>
            <path
              d={`M0,${20 + Math.sin(i) * 10} Q25,${10 + Math.sin(i + 1) * 10} 50,${20 + Math.sin(i + 2) * 8} T100,${15 + Math.sin(i + 3) * 10}`}
              fill="none"
              stroke="rgba(96,165,250,0.2)"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
      ))}
    </div>
  );
}

export function SkeletonKPIRow() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 animate-fade-in">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="bg-mono-900/40 border border-mono-700/50 border-t-2 border-t-mono-600/60 rounded-lg px-4 py-3">
          <SkeletonBar className="h-2 w-20 mb-3" />
          <SkeletonBar className="h-5 w-14" />
        </div>
      ))}
    </div>
  );
}